import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, Typography, IconButton, Tooltip } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';
import GradientCircularProgress from '../../../components/common/GradientCircularProgress';
import useBackendApi from '../../../hooks/useBackendApi';
import { AccountType } from '../../../types/account';
import { AppointmentEvent } from '../../../types/appointment';
import { AppointmentEventCard } from '../../../frost/AppointmentsList';
import AccountNameDescription from './account/AccountNameDescription';

import AccountDrawer, { AccountDrawerPayload } from './AccountDrawer';
import { RequestOptions } from '../../../types/httpRequests';
import { AccountEventsResponse } from '../../../types/api/accountEvents';
import { AddressBlock, ContactBlock } from './account/AccountNameDescription';
import { ghostActionIconButtonSx } from '../../../components/common/buttons/actionIconButtonStyles';
import { defaultSiteTitle } from '../../../constants/shop';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
};

type AccountDetailsResponse = {
  message?: string;
  status_code?: number;
  detail?: string;
  account?: AccountType;
  user_org_roles?: string[];
  user_acc_roles?: string[];
};

export default function Account(): JSX.Element {
  const { org_path_name, acc_path_name } = useParams<RouteParams>();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const [account, setAccount] = useState<AccountType | null>(null);
  const [accountEvents, setAccountEvents] = useState<AppointmentEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [userOrgRoles, setUserOrgRoles] = useState<string[]>([]);
  const [userAccRoles, setUserAccRoles] = useState<string[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fail = useCallback(() => setErrorText('Something went wrong. Please try again.'), []);

  const handleCommonAuthNav = useCallback(
    (resStatus?: number, body?: { status_code?: number; message?: string }) => {
      if (
        resStatus === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'UNAUTHORIZED' ||
        body?.message === 'NO_ACCESS'
      ) {
        navigate('/401.html');
        return true;
      }
      if (resStatus === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        navigate('/404.html');
        return true;
      }
      return false;
    },
    [navigate],
  );

  const isOrgAdmin = useMemo(() => userOrgRoles.some((r) => r?.toLowerCase() === 'admin'), [userOrgRoles]);
  const isAccAdmin = useMemo(() => userAccRoles.some((r) => r?.toLowerCase() === 'admin'), [userAccRoles]);

  const canEditAccount = isOrgAdmin || isAccAdmin;

  const fetchAccount = useCallback(async () => {
    if (!org_path_name || !acc_path_name) return;

    try {
      const res = await apiFetchRequest({
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}`,
        method: 'GET',
      });

      if (!res) {
        fail();
        return;
      }

      const body = res.body as AccountDetailsResponse | undefined;
      if (handleCommonAuthNav(res.status, body)) return;

      if (res.isError || res.status !== 200) {
        fail();
        return;
      }

      if (body?.account) {
        setAccount(body.account);
        setUserOrgRoles(Array.isArray(body.user_org_roles) ? body.user_org_roles : []);
        setUserAccRoles(Array.isArray(body.user_acc_roles) ? body.user_acc_roles : []);
        setErrorText(null);
        return;
      }

      fail();
    } catch {
      fail();
    }
  }, [acc_path_name, apiFetchRequest, fail, handleCommonAuthNav, org_path_name]);

  const fetchAccEvents = useCallback(async () => {
    if (!org_path_name || !acc_path_name) return;
    setEventsLoading(true);
    setEventsError(null);

    const accEventsEndpoint = `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
      acc_path_name,
    )}/events/?limit=5`;
    const opts: RequestOptions = {
      endpoint: accEventsEndpoint,
      method: 'GET',
    };

    try {
      const res = await apiFetchRequest(opts);

      if (!res) {
        setEventsError('Something went wrong. Please try again.');
        return;
      }

      const { body, status, isError } = res;
      const typedBody = body as AccountEventsResponse | undefined;

      const unauthorized =
        status === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'UNAUTHORIZED' ||
        body?.message === 'NO_ACCESS';

      if (unauthorized) {
        navigate('/401.html');
        return;
      }

      if (status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        setAccountEvents([]);
        return;
      }

      if (isError || status !== 200) {
        setEventsError('Something went wrong. Please try again.');
        return;
      }

      const events = Array.isArray(typedBody?.events) ? typedBody?.events : [];
      if (!events) {
        setAccountEvents([]);
        return;
      }
      const mappedEvents: AppointmentEvent[] = events.map((event) => ({
        acc_event_id: event.acc_event_id,
        event_type: event.event_type ?? null,
        title: event.title ?? null,
        location_name: event.location_name ?? null,
        event_start_date: event.event_start_date ?? '',
        event_end_date: event.event_end_date ?? '',
        description: event.description ?? null,
      }));
      setAccountEvents(mappedEvents);
    } catch {
      setEventsError('Something went wrong. Please try again.');
    } finally {
      setEventsLoading(false);
    }
  }, [acc_path_name, apiFetchRequest, fail, navigate, org_path_name]);

  useEffect(() => {
    if (!org_path_name || !acc_path_name) {
      navigate('/404.html');
      return;
    }
    void fetchAccount();
    void fetchAccEvents();
  }, [acc_path_name, fetchAccEvents, fetchAccount, navigate, org_path_name]);

  useEffect(() => {
    if (account?.name) {
      document.title = `${account.name} | Account Details | ${defaultSiteTitle}`;
    }
  }, [account?.name]);

  const handleOpenEditDrawer = useCallback(() => setIsEditDrawerOpen(true), []);

  const handleCloseEditDrawer = useCallback(() => {
    if (isSubmitting) return;
    setIsEditDrawerOpen(false);
  }, [isSubmitting]);

  const handleUpdateAccount = useCallback(
    async (payload: AccountDrawerPayload) => {
      if (!org_path_name || !acc_path_name) return;
      if (!canEditAccount) return;

      setIsSubmitting(true);

      try {
        const res = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}`,
          method: 'PUT',
          body: payload,
        });

        if (!res) {
          fail();
          return;
        }

        const body = res.body as { message?: string; detail?: string; status_code?: number };
        if (handleCommonAuthNav(res.status, body)) return;

        if (res.isError || (res.status !== 200 && res.status !== 204) || body?.message !== 'SUCCESS') {
          fail();
          return;
        }

        setIsEditDrawerOpen(false);

        const nextPath = payload.path_name?.trim() || acc_path_name;
        if (nextPath !== acc_path_name) {
          navigate(`/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(nextPath)}`, { replace: true });
          return;
        }

        await fetchAccount();
      } catch {
        fail();
      } finally {
        setIsSubmitting(false);
      }
    },
    [acc_path_name, apiFetchRequest, canEditAccount, fail, fetchAccount, handleCommonAuthNav, navigate, org_path_name],
  );

  if ((isLoading && !account && !errorText) || !(acc_path_name && org_path_name)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography color="error">{errorText}</Typography>
      </Box>
    );
  }

  if (!account) return <></>;

  const routes = {
    staff: `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/staff`,
    locations: `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/locations`,
    events: `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events`,
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'transparent',
          borderRadius: '16px',
          padding: '0px 40px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'wrap',
          mt: 0,
          width: '100%',
        }}
      >
        <AccountNameDescription
          account={account}
          variant="card"
          actionButton={
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              {canEditAccount && (
                <Tooltip title="Edit Account" arrow>
                  <span>
                    <IconButton
                      aria-label="Edit Account"
                      onClick={handleOpenEditDrawer}
                      disabled={isSubmitting}
                      sx={ghostActionIconButtonSx(theme.images.editIcon)}
                    />
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Staff" arrow>
                <span>
                  <IconButton
                    aria-label="Staff"
                    onClick={() => navigate(routes.staff)}
                    sx={ghostActionIconButtonSx(theme.images.peopleIcon)}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Locations" arrow>
                <span>
                  <IconButton
                    aria-label="Locations"
                    onClick={() => navigate(routes.locations)}
                    sx={ghostActionIconButtonSx(theme.images.locationIcon)}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Events" arrow>
                <span>
                  <IconButton
                    aria-label="Events"
                    onClick={() => navigate(routes.events)}
                    sx={ghostActionIconButtonSx(theme.images.calendarIcon)}
                  />
                </span>
              </Tooltip>
            </Box>
          }
        />
      </Box>

      <Box
        sx={{ p: 2 }}
        style={{
          backgroundColor: theme.layout.defaults.backgroundColor,
          borderRadius: '16px',
          padding: '40px',
          width: '100%',
        }}
      >
        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} md={8} mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Recent events
            </Typography>
            {eventsLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading events...
              </Typography>
            ) : eventsError ? (
              <Typography variant="body2" color="error">
                {eventsError}
              </Typography>
            ) : accountEvents.length ? (
              <Box>
                {accountEvents.map((evt) => (
                  <Box
                    key={evt.acc_event_id ?? `${evt.event_start_date}-${evt.title}-${evt.location_name}`}
                    onClick={() => {
                      if (evt.acc_event_id) {
                        navigate(
                          `/o/${encodeURIComponent(org_path_name || '')}/a/${encodeURIComponent(
                            acc_path_name || '',
                          )}/events/${encodeURIComponent(String(evt.acc_event_id))}`,
                        );
                      }
                    }}
                    sx={{
                      cursor: evt.acc_event_id ? 'pointer' : 'default',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': evt.acc_event_id
                        ? {
                            transform: 'translateY(-2px)',
                            '& > div': {
                              borderColor: theme.palette.primary.main,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            },
                          }
                        : {},
                    }}
                  >
                    <AppointmentEventCard event={evt} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No recent events found.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Account Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <ContactBlock
                title="Primary Contact"
                name={account.contact_1_name}
                email={account.contact_1_email}
                phone={account.contact_1_phone}
              />
              <ContactBlock
                title="Secondary Contact"
                name={account.contact_2_name}
                email={account.contact_2_email}
                phone={account.contact_2_phone}
              />
              <AddressBlock
                line1={account.address_line_1}
                line2={account.address_line_2}
                city={account.address_city}
                state={account.address_state}
                zip={account.address_zip}
              />
            </Box>
          </Grid>
        </Grid>

        <AccountDrawer
          open={isEditDrawerOpen}
          mode="edit"
          initialAccount={account}
          onClose={handleCloseEditDrawer}
          onSubmit={handleUpdateAccount}
          isSubmitting={isSubmitting}
        />
      </Box>
    </>
  );
}
